// Diagnoses WHY a name failed NAME_PATTERN (validateName.js) so the UI can show
// the offending character and telemetry can log its code point — never the raw name.

const ALLOWED_NAME_CHAR_PATTERN = /[A-Za-z -]/;

const MAX_REPORTED_INVALID_CHARS = 5;

// Common invisible / look-alike culprits. Unlabeled chars still get char + code.
const INVALID_CHAR_LABELS = {
  0x0009: 'tab',
  0x00a0: 'non-breaking space',
  0x2018: 'curly apostrophe',
  0x2019: 'curly apostrophe',
  0x2013: 'en dash',
  0x2014: 'em dash',
};

// Shared with buildValidationErrorSummary: the exact phrases below are embedded in
// the UI error message and scanned back out for telemetry. Keep them in sync.
const STRUCTURAL_CAUSE_MESSAGES = {
  'trailing-space': 'must not end with a trailing space',
  'trailing-hyphen': 'must not end with a trailing hyphen',
  'leading-space': 'must not start with a space',
  'leading-hyphen': 'must not start with a hyphen',
  'consecutive-separators': 'must not contain consecutive spaces or hyphens',
};

const CODE_POINT_PATTERN = /U\+[0-9A-F]{4,6}/g;

const formatCodePoint = (codePoint) => `U+${codePoint.toString(16).toUpperCase().padStart(4, '0')}`;

const findStructuralCause = (value) => {
  const firstChar = value[0];
  const lastChar = value[value.length - 1];

  if (lastChar === ' ') return 'trailing-space';
  if (lastChar === '-') return 'trailing-hyphen';
  if (firstChar === ' ') return 'leading-space';
  if (firstChar === '-') return 'leading-hyphen';
  if (/[ -]{2}/.test(value)) return 'consecutive-separators';

  return null;
};

const ALLOWED_CHARS_SUFFIX = 'Only letters, spaces, and hyphens are allowed.';

// Returns the user-facing error for a failed name, or null when the diagnosis is
// empty (caller falls back to the generic message).
const buildNameFailureMessage = ({ invalidChars, structuralCause }) => {
  if (invalidChars && invalidChars.length > 0) {
    const { char, code, index, label } = invalidChars[0];
    const codeWithLabel = label ? `${code}, ${label}` : code;
    return `The name contains an unsupported character '${char}' (${codeWithLabel}) at position ${index + 1}. ${ALLOWED_CHARS_SUFFIX}`;
  }

  if (structuralCause) {
    return `The name ${STRUCTURAL_CAUSE_MESSAGES[structuralCause]}. ${ALLOWED_CHARS_SUFFIX}`;
  }

  return null;
};

const diagnoseNameFailure = (value) => {
  const emptyDiagnosis = { invalidChars: [], structuralCause: null };

  if (typeof value !== 'string' || !value) return emptyDiagnosis;

  // Array.from iterates code points, so surrogate pairs report one correct code.
  const invalidChars = [];
  Array.from(value).forEach((char, index) => {
    if (ALLOWED_NAME_CHAR_PATTERN.test(char)) return;
    if (invalidChars.length >= MAX_REPORTED_INVALID_CHARS) return;

    const codePoint = char.codePointAt(0);
    const label = INVALID_CHAR_LABELS[codePoint];
    invalidChars.push({
      char,
      code: formatCodePoint(codePoint),
      index,
      ...(label ? { label } : {}),
    });
  });

  if (invalidChars.length > 0) return { invalidChars, structuralCause: null };

  return { invalidChars: [], structuralCause: findStructuralCause(value) };
};

export {
  formatCodePoint,
  buildNameFailureMessage,
  ALLOWED_NAME_CHAR_PATTERN,
  STRUCTURAL_CAUSE_MESSAGES,
  CODE_POINT_PATTERN,
};
export default diagnoseNameFailure;
